import { DEFAULT_WORKSPACE_DASHBOARD } from "./dashboard-defaults.ts";
import type { DashboardBlock, WorkspaceDashboardConfig } from "./index.ts";

export function blocksOverlap(a: DashboardBlock["layout"], b: DashboardBlock["layout"]) {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}
export const readingOrder = (blocks: DashboardBlock[]) => [...blocks].sort((a, b) => a.layout.y - b.layout.y || a.layout.x - b.layout.x || a.id.localeCompare(b.id));

/** A filtered/grouped projection does not reveal hidden coordinates or mutate the shared layout. */
export function packDashboardBlocks(blocks: DashboardBlock[]): DashboardBlock[] {
  const packed: DashboardBlock[] = [];
  for (const b of readingOrder(blocks)) {
    let placed = false;
    for (let y = 0; y <= 999 && !placed; y++) for (let x = 0; x <= 12 - b.layout.w; x++) {
      const layout = { ...b.layout, x, y };
      if (packed.some(p => blocksOverlap(p.layout, layout))) continue;
      packed.push({ ...b, layout }); placed = true; break;
    }
  }
  return packed;
}

/** Smallest and largest block in grid cells; a trend needs room for its chart. */
export const blockSizeLimits = (type: DashboardBlock["type"]) => ({ minW: type === "trend" ? 6 : 3, minH: type === "trend" ? 3 : 2, maxW: 12, maxH: 6 });

/** Move one block and push collisions down in stable order. Inputs stay unchanged. */
export function placeDashboardBlock(blocks: DashboardBlock[], id: string, change: Partial<DashboardBlock["layout"]>) {
  const original = blocks.find(b => b.id === id);
  if (!original) return blocks;
  const size = blockSizeLimits(original.type);
  const w = Math.max(size.minW, Math.min(size.maxW, change.w ?? original.layout.w));
  const h = Math.max(size.minH, Math.min(size.maxH, change.h ?? original.layout.h));
  const moved = { ...original, layout: { x: Math.max(0, Math.min(12 - w, change.x ?? original.layout.x)), y: Math.max(0, Math.min(999, change.y ?? original.layout.y)), w, h } };
  const placed = [moved];
  for (const b of readingOrder(blocks.filter(b => b.id !== id))) {
    const next = { ...b, layout: { ...b.layout } };
    while (placed.some(p => blocksOverlap(p.layout, next.layout))) {
      if (++next.layout.y > 999) return blocks;
    }
    placed.push(next);
  }
  return blocks.map(b => placed.find(p => p.id === b.id)!);
}

/** Deterministic compatibility projection; a v2 layout is written only on explicit save. */
export function legacyDashboardBlocks(config: WorkspaceDashboardConfig, products: { product_id: string; metric_keys: string[] }[]): DashboardBlock[] {
  if (config.blocks) return config.blocks;
  const inbox: DashboardBlock = { id: "inbox", type: "inbox", product_id: null, metric_key: null, layout: { x: 0, y: 0, w: 4, h: 3 } };
  const metrics: DashboardBlock[] = products.flatMap((p, pi) => p.metric_keys.map((key, i) => ({ id: `metric_${pi}_${i}`, type: "metric" as const, product_id: p.product_id, metric_key: key, layout: { x: 0, y: i + 1, w: 4, h: 2 } })));
  const ordered = config.panel_order[0] === "inbox" ? [inbox, ...metrics] : [...metrics, inbox];
  return packDashboardBlocks(ordered.slice(0, 100).map((b, i) => ({ ...b, layout: { ...b.layout, x: 0, y: i } })));
}

/**
 * What a workspace sees before anyone saves a layout (docs/design-system.md
 * §0.4i, 2026-11-01). With one product that has metrics: its primary metric
 * as a trend across the width, then its other metrics as numbers, three to a
 * row. The inbox already has its place in the sidebar; a block for it is one
 * "Add block" away. Otherwise, the inbox and then each product's metrics.
 */
export function defaultDashboardBlocks(products: { product_id: string; metric_keys: string[]; primary_metric_key?: string | null }[]): DashboardBlock[] {
  const only = products.length === 1 ? products[0] : undefined;
  if (!only?.metric_keys.length) return legacyDashboardBlocks(DEFAULT_WORKSPACE_DASHBOARD, products);
  const primary = only.primary_metric_key && only.metric_keys.includes(only.primary_metric_key) ? only.primary_metric_key : only.metric_keys[0]!;
  const trend: DashboardBlock = { id: "trend_0", type: "trend", product_id: only.product_id, metric_key: primary, layout: { x: 0, y: 0, w: 12, h: 3 } };
  const numbers = only.metric_keys.filter(key => key !== primary).slice(0, 99).map((key, i): DashboardBlock =>
    ({ id: `metric_0_${i}`, type: "metric", product_id: only.product_id, metric_key: key, layout: { x: (i % 3) * 4, y: 3 + Math.floor(i / 3) * 2, w: 4, h: 2 } }));
  return [trend, ...numbers];
}
